/**
 * Procedural "neon ping" for new activity-feed events.
 *
 * The sound is synthesized on the fly with the Web Audio API (no asset files),
 * so it stays theme-aware and adds nothing to the bundle. Frequencies track the
 * shell's brand hues: cyan in dark mode, warmer violet in light mode.
 */

type PingTheme = "light" | "dark";

/** Minimum gap between pings so an SSE burst doesn't machine-gun the user. */
export const NOTIFICATION_SOUND_DEBOUNCE_MS = 2500;

interface PingFrequencies {
  /** Starting pitch of the glide. */
  base: number;
  /** Peak pitch reached mid-glide. */
  peak: number;
}

/**
 * Lower, musical fundamentals for a deep, vibrant tone. Dark leans a little
 * brighter (cyan); light is warmer and lower (violet). Kept well below the
 * airy register that made the earlier ping sound whistly.
 */
export function notificationPingFrequencies(theme: PingTheme): PingFrequencies {
  return theme === "dark" ? { base: 392, peak: 523 } : { base: 330, peak: 440 };
}

interface ShouldPlayOptions {
  /** The user's opt-in preference. */
  enabled: boolean;
  /** `document.hidden` — never play for a backgrounded tab. */
  documentHidden: boolean;
  /** `prefers-reduced-motion: reduce` — respect the accessibility opt-out. */
  reducedMotion: boolean;
  /** Timestamp (ms) of the last ping, or null if none yet. */
  lastPlayedAt: number | null;
  /** Current time (ms). */
  now: number;
}

/** Pure guard predicate — unit-testable without touching the Web Audio API. */
export function shouldPlayNotificationSound(opts: ShouldPlayOptions): boolean {
  if (!opts.enabled) return false;
  if (opts.documentHidden) return false;
  if (opts.reducedMotion) return false;
  if (
    opts.lastPlayedAt !== null &&
    opts.now - opts.lastPlayedAt < NOTIFICATION_SOUND_DEBOUNCE_MS
  ) {
    return false;
  }
  return true;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor })
      .webkitAudioContext ??
    null
  );
}

let audioContext: AudioContext | null = null;
let lastPlayedAt: number | null = null;

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    audioContext = new Ctor();
  } catch {
    return null;
  }
  return audioContext;
}

/**
 * Resume the shared AudioContext from within a user gesture. Browsers keep the
 * context suspended until the first interaction, so call this on pointer/key
 * events and when the user hits "Play sample" in preferences.
 */
export function unlockNotificationAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    void ctx.resume();
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Play a single ~600 ms ping. Returns silently if audio is unavailable or the
 * context can't be created. Does not itself check the user preference — callers
 * gate on {@link shouldPlayNotificationSound}; only reduced motion and context
 * availability are enforced here so the preview button can bypass debounce.
 */
export function playTwidoNotificationPing(theme: PingTheme): void {
  if (prefersReducedMotion()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") void ctx.resume();

  const { base, peak } = notificationPingFrequencies(theme);
  const t0 = ctx.currentTime;
  const end = t0 + 0.6;

  // Warm low-pass keeps the tone rounded — the high air is what whistled.
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.setValueAtTime(2600, t0);
  lowpass.frequency.exponentialRampToValueAtTime(1400, end);
  lowpass.Q.value = 0.9;
  lowpass.connect(ctx.destination);

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, t0);
  master.gain.exponentialRampToValueAtTime(0.14, t0 + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, end);
  master.connect(lowpass);

  // Core tone — triangle for a fuller, richer body than a bare sine.
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(base, t0);
  osc.frequency.exponentialRampToValueAtTime(peak, t0 + 0.1);
  osc.frequency.exponentialRampToValueAtTime(peak * 0.96, t0 + 0.5);
  osc.connect(master);

  // Detuned twin — slight chorus for vibrance and movement.
  const oscDetune = ctx.createOscillator();
  oscDetune.type = "triangle";
  oscDetune.detune.value = 9;
  oscDetune.frequency.setValueAtTime(base, t0);
  oscDetune.frequency.exponentialRampToValueAtTime(peak, t0 + 0.1);
  oscDetune.frequency.exponentialRampToValueAtTime(peak * 0.96, t0 + 0.5);
  const detuneGain = ctx.createGain();
  detuneGain.gain.value = 0.6;
  oscDetune.connect(detuneGain).connect(master);

  // Sub — an octave below for depth and weight.
  const sub = ctx.createOscillator();
  sub.type = "sine";
  sub.frequency.value = base / 2;
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.0001, t0);
  subGain.gain.exponentialRampToValueAtTime(0.5, t0 + 0.03);
  subGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
  sub.connect(subGain).connect(master);

  osc.start(t0);
  oscDetune.start(t0);
  sub.start(t0);
  osc.stop(end);
  oscDetune.stop(end);
  sub.stop(t0 + 0.42);
}

/**
 * Play a ping for a new feed event, applying all runtime guards (preference,
 * visibility, reduced motion, debounce). Records the play time so bursts are
 * throttled. No-op when any guard fails.
 */
export function playNotificationSoundForEvent(
  theme: PingTheme,
  enabled: boolean,
): void {
  const now = Date.now();
  const ok = shouldPlayNotificationSound({
    enabled,
    documentHidden:
      typeof document !== "undefined" ? document.hidden : false,
    reducedMotion: prefersReducedMotion(),
    lastPlayedAt,
    now,
  });
  if (!ok) return;
  lastPlayedAt = now;
  playTwidoNotificationPing(theme);
}
